import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  Calendar as CalendarIcon, 
  Clock, 
  MapPin, 
  Phone, 
  Mail, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Loader2,
  ArrowLeft,
  Edit,
  Trash2
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

const ManageBooking = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [staffName, setStaffName] = useState(null);
  const [error, setError] = useState(null);
  
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  
  const [showModifyDialog, setShowModifyDialog] = useState(false);
  const [newDate, setNewDate] = useState(null);

  useEffect(() => {
    fetchBooking();
  }, [token]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/public/booking/${token}`);
      setBooking(response.data.booking);
      setBusiness(response.data.business);
      setServices(response.data.services || []);
      setStaffName(response.data.staff_name);
    } catch (err) {
      setError(err.response?.data?.detail || "Prenotazione non trovata");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/public/booking/${token}/cancel`, { reason: cancelReason });
      toast.success("Prenotazione cancellata con successo");
      fetchBooking();
      setShowCancelDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella cancellazione");
    } finally {
      setCancelling(false);
    }
  };

  const parseDateTime = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr.replace("Z", ""));
    } catch {
      return null;
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      confirmed: { label: "Confermata", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
      pending: { label: "In attesa", color: "bg-amber-100 text-amber-700", icon: Clock },
      cancelled: { label: "Cancellata", color: "bg-red-100 text-red-700", icon: XCircle },
      completed: { label: "Completata", color: "bg-blue-100 text-blue-700", icon: CheckCircle },
      no_show: { label: "Non presentato", color: "bg-slate-100 text-slate-700", icon: XCircle }
    };
    return statusMap[status] || statusMap.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Prenotazione non trovata</h2>
            <p className="text-slate-500 mb-4">{error}</p>
            <Link to="/">
              <Button>Torna alla Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const startDate = parseDateTime(booking?.start_datetime);
  const endDate = parseDateTime(booking?.end_datetime);
  const statusInfo = getStatusInfo(booking?.status);
  const StatusIcon = statusInfo.icon;
  const isCancelled = booking?.status === "cancelled";
  const isPast = startDate && startDate < new Date();

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Gestisci Prenotazione</h1>
          <p className="text-slate-500 text-sm mt-1">{business?.name}</p>
        </div>

        {/* Booking Card */}
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Dettagli Prenotazione</CardTitle>
              <Badge className={statusInfo.color}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusInfo.label}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Date & Time */}
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <CalendarIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">
                  {startDate && format(startDate, "EEEE d MMMM yyyy", { locale: it })}
                </p>
                <p className="text-sm text-slate-500">
                  {startDate && format(startDate, "HH:mm")} - {endDate && format(endDate, "HH:mm")}
                </p>
              </div>
            </div>

            {/* Services */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Servizi prenotati:</p>
              <ul className="space-y-1">
                {services.map((svc, i) => (
                  <li key={i} className="flex justify-between text-sm">
                    <span>{svc.name}</span>
                    <span className="text-slate-500">{svc.duration_minutes} min - €{svc.price?.toFixed(2)}</span>
                  </li>
                ))}
              </ul>
              {booking?.total_price > 0 && (
                <p className="text-right font-semibold mt-2">Totale: €{booking.total_price.toFixed(2)}</p>
              )}
            </div>

            {/* Staff */}
            {staffName && (
              <div className="border-t pt-4">
                <p className="text-sm"><strong>Operatore:</strong> {staffName}</p>
              </div>
            )}

            {/* Customer Info */}
            <div className="border-t pt-4">
              <p className="text-sm"><strong>Nome:</strong> {booking?.customer_name}</p>
              {booking?.customer_email && (
                <p className="text-sm"><strong>Email:</strong> {booking.customer_email}</p>
              )}
              {booking?.customer_phone && (
                <p className="text-sm"><strong>Telefono:</strong> {booking.customer_phone}</p>
              )}
            </div>

            {/* Business Contact */}
            {business && (
              <div className="border-t pt-4 bg-slate-50 -mx-6 px-6 py-3 -mb-6 rounded-b-lg">
                <p className="text-sm font-medium mb-2">{business.name}</p>
                {business.address && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> {business.address}, {business.city}
                  </p>
                )}
                {business.phone && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Phone className="h-3 w-3" /> {business.phone}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {!isCancelled && !isPast && (
          <div className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => setShowCancelDialog(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Cancella Prenotazione
            </Button>
          </div>
        )}

        {/* Review Link for past bookings */}
        {isPast && !isCancelled && (
          <Card className="mt-4 bg-primary/5 border-primary/20">
            <CardContent className="py-4 text-center">
              <p className="text-sm text-slate-600 mb-3">Com'è andata? Lascia una recensione!</p>
              <Link to={`/review/${token}`}>
                <Button>Lascia una Recensione</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Cancel Dialog */}
        {showCancelDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="max-w-md w-full">
              <CardHeader>
                <CardTitle className="text-red-600 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Conferma Cancellazione
                </CardTitle>
                <CardDescription>
                  Sei sicuro di voler cancellare questa prenotazione? L'azione non può essere annullata.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Motivo (opzionale)</label>
                  <Textarea 
                    placeholder="Perché stai cancellando?"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowCancelDialog(false)}
                  >
                    Annulla
                  </Button>
                  <Button 
                    variant="destructive" 
                    className="flex-1"
                    onClick={handleCancel}
                    disabled={cancelling}
                  >
                    {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageBooking;
